import { useState, useCallback, useRef, useEffect } from "react";

interface UseImageWithRetryOptions {
  maxRetries?: number;
  onSuccess?: () => void;
  onMaxRetriesReached?: () => void;
}

interface UseImageWithRetryReturn {
  imageKey: number;
  isGenerating: boolean;
  isPermanentlyBroken: boolean;
  retryCount: number;
  handleImageError: () => void;
  handleImageLoad: () => void;
  reset: () => void;
}

/**
 * Custom hook for handling image loading with exponential backoff retry
 * Useful for images that might not be ready immediately (e.g., AWS Lambda thumbnails)
 *
 * @example
 * const { imageKey, isGenerating, handleImageError, handleImageLoad } = useImageWithRetry({
 *   maxRetries: 8,
 *   onSuccess: () => console.log('Image loaded!'),
 *   onMaxRetriesReached: () => console.warn('Image failed to load')
 * });
 *
 * <img key={imageKey} src={url} onError={handleImageError} onLoad={handleImageLoad} />
 */
export const useImageWithRetry = (options: UseImageWithRetryOptions = {}): UseImageWithRetryReturn => {
  const {
    maxRetries = 8, // ~2 minutes total with exponential backoff
    onSuccess,
    onMaxRetriesReached
  } = options;

  const [imageKey, setImageKey] = useState(0);
  const [retryCount, setRetryCount] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPermanentlyBroken, setIsPermanentlyBroken] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  function clearRetryTimeout() {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  }

  const handleImageError = useCallback(() => {
    if (retryCount < maxRetries) {
      setIsGenerating(true);

      // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 32s, 64s, 128s
      const delay = Math.min(1000 * Math.pow(2, retryCount), 128000);

      console.log(`[useImageWithRetry] Retry ${retryCount + 1}/${maxRetries} - waiting ${delay}ms`);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef?.current);
      }

      timeoutRef.current = setTimeout(() => {
        setRetryCount((prev) => prev + 1);
        setImageKey((prev) => prev + 1); // Force image reload
        timeoutRef.current = undefined;
      }, delay);
    } else {
      // Max retries reached
      console.warn(`[useImageWithRetry] Max retries (${maxRetries}) reached`);
      setIsGenerating(false);
      setIsPermanentlyBroken(true);
      onMaxRetriesReached?.();
    }
  }, [retryCount, maxRetries, onMaxRetriesReached]);

  const handleImageLoad = useCallback(() => {
    console.log("[useImageWithRetry] Image loaded successfully");

    // clear any pending retries
    clearRetryTimeout();

    setIsGenerating(false);
    setIsPermanentlyBroken(false);
    onSuccess?.();
  }, [onSuccess]);

  const reset = useCallback(() => {
    clearRetryTimeout();

    setImageKey(0);
    setRetryCount(0);
    setIsGenerating(false);
    setIsPermanentlyBroken(false);
  }, []);

  return {
    imageKey,
    isGenerating,
    isPermanentlyBroken,
    retryCount,
    handleImageError,
    handleImageLoad,
    reset
  };
};
